
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, RefreshCw, Eye, EyeOff, Download } from "lucide-react";

interface FreeSample {
  id: string;
  title: string;
  description: string | null;
  zip_url: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const FreeSamplesManager = () => {
  const [samples, setSamples] = useState<FreeSample[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSample, setEditingSample] = useState<FreeSample | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    zip_url: "",
    display_order: 0,
    is_active: true,
  });
  const { toast } = useToast();

  const fetchSamples = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('free_samples')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setSamples(data || []);
    } catch (error: any) {
      console.error('Error fetching samples:', error);
      toast({
        title: "Error",
        description: "Failed to fetch free samples",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSamples();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingSample) {
        const { error } = await supabase
          .from('free_samples')
          .update({
            ...formData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingSample.id);

        if (error) throw error;
        toast({ title: "Success", description: "Free sample updated successfully" });
      } else {
        const { error } = await supabase
          .from('free_samples')
          .insert([formData]);

        if (error) throw error;
        toast({ title: "Success", description: "Free sample created successfully" });
      }

      setDialogOpen(false);
      setEditingSample(null);
      setFormData({
        title: "",
        description: "",
        zip_url: "",
        display_order: 0,
        is_active: true,
      });
      fetchSamples();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save free sample",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (sample: FreeSample) => {
    setEditingSample(sample);
    setFormData({
      title: sample.title,
      description: sample.description || "",
      zip_url: sample.zip_url,
      display_order: sample.display_order,
      is_active: sample.is_active,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this free sample?')) return;

    try {
      const { error } = await supabase
        .from('free_samples')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: "Success", description: "Free sample deleted successfully" });
      fetchSamples();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete free sample",
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('free_samples')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      toast({ 
        title: "Success", 
        description: `Free sample ${!currentStatus ? 'activated' : 'deactivated'} successfully` 
      });
      fetchSamples();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update free sample status",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-slate-800">Free Samples</h3>
          <p className="text-slate-600">Manage free sample downloads</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchSamples} disabled={loading} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Free Sample
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingSample ? 'Edit Free Sample' : 'Add New Free Sample'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">ZIP Download URL</label>
                  <Input
                    type="url"
                    value={formData.zip_url}
                    onChange={(e) => setFormData({...formData, zip_url: e.target.value})}
                    placeholder="https://example.com/sample.zip"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Display Order</label>
                  <Input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
                    min="0"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="rounded"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium">Active</label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingSample ? 'Update' : 'Create'} Free Sample
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Free Samples List ({samples.length} items)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>ZIP URL</TableHead>
                  <TableHead>Display Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {samples.map((sample) => (
                  <TableRow key={sample.id}>
                    <TableCell className="font-medium">{sample.title}</TableCell>
                    <TableCell className="max-w-xs truncate">{sample.description || 'No description'}</TableCell>
                    <TableCell>
                      <a
                        href={sample.zip_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </a>
                    </TableCell>
                    <TableCell>{sample.display_order}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant={sample.is_active ? "default" : "secondary"}
                        onClick={() => toggleActive(sample.id, sample.is_active)}
                        className="w-20"
                      >
                        {sample.is_active ? (
                          <>
                            <Eye className="w-3 h-3 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-3 h-3 mr-1" />
                            Inactive
                          </>
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(sample)}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(sample.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FreeSamplesManager;
